/**
 * Phase 19.5 — Producer Statistics Dashboard.
 *
 * Standalone surface at `/producer/stats` showing the producer's own beat
 * analytics (plays, downloads, likes, comments, community shares, gifts
 * received, sales, conversion rate). Anyone with the link can view a
 * specific producer's stats via `/producer/{user_id}/stats`.
 *
 * Kept SEPARATE from the artist analytics dashboard — this is a
 * Producer Department surface.
 */
import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import SharedNavigation from "@/components/SharedNavigation";
import api from "@/lib/api";
import { useCurrentUser } from "@/lib/useCurrentUser";
import { toast } from "sonner";

const StatTile = ({ icon, label, value, accent, testId, sub }) => (
  <Card
    className={`p-4 ${accent || "bg-white/[0.03] border-white/10"}`}
    data-testid={testId}
  >
    <div className="flex items-center gap-2">
      <span className="text-lg" aria-hidden>{icon}</span>
      <p className="text-[11px] uppercase tracking-widest text-gray-400">{label}</p>
    </div>
    <p className="text-2xl font-black text-white mt-2">{value ?? "—"}</p>
    {sub && <p className="text-[11px] text-gray-500 mt-1">{sub}</p>}
  </Card>
);

const ProducerStatsPage = () => {
  const { producerId } = useParams();
  const { user: me } = useCurrentUser();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const isOwn = !producerId || (me?.user_id && producerId === me.user_id);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    setError(null);
    const url = isOwn ? "/producer/stats" : `/producer/${producerId}/stats`;
    api.get(url)
      .then((r) => { if (alive) setStats(r.data); })
      .catch((e) => {
        if (alive) {
          const detail = e?.response?.data?.detail;
          setError(detail || "Could not load producer stats");
          if (e?.response?.status === 401) {
            toast.error("Sign in to see your producer stats");
          }
        }
      })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [producerId, isOwn]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#08080c] flex items-center justify-center" data-testid="producer-stats-loading">
        <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-purple-500" />
      </div>
    );
  }

  if (error || !stats) {
    return (
      <div className="min-h-screen bg-[#08080c] flex items-center justify-center p-6">
        <Card className="max-w-md p-6 bg-white/5 border-rose-500/30" data-testid="producer-stats-error">
          <h1 className="text-xl font-black text-white">🎹 Producer Stats</h1>
          <p className="text-rose-300 mt-2">{error || "Stats unavailable."}</p>
          {!me && <Link to="/login" className="text-purple-300 underline mt-2 inline-block">Sign in</Link>}
        </Card>
      </div>
    );
  }

  const revenueDollars = (stats.sales_revenue_cents || 0) / 100;
  const giftCoinValue = stats.gift_coins_received || 0;

  return (
    <div className="min-h-screen bg-[#08080c]" data-testid="producer-stats-page">
      <SharedNavigation currentUser={me} />
      <div className="pt-24 pb-16 px-4 sm:px-6 lg:px-8 max-w-5xl mx-auto">
        <div className="mb-8">
          <h1 className="text-3xl sm:text-4xl font-black text-white flex items-center gap-3">
            🎹 <span>Producer Stats</span>
          </h1>
          <p className="text-sm text-gray-400 mt-1">
            {isOwn ? "Your" : "This producer's"} beats — engagement, downloads, gifts, and revenue. Kept separate from artist analytics.
          </p>
        </div>

        {/* Headline tiles */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 mb-6">
          <StatTile icon="🎹" label="Beats" value={stats.beat_count?.toLocaleString?.()} testId="ps-beat-count" />
          <StatTile icon="▶" label="Plays" value={stats.plays?.toLocaleString?.()} testId="ps-plays" />
          <StatTile icon="❤️" label="Likes" value={stats.likes?.toLocaleString?.()} testId="ps-likes" />
          <StatTile icon="💬" label="Comments" value={stats.comments?.toLocaleString?.()} testId="ps-comments" />
          <StatTile icon="⬇" label="Downloads" value={stats.downloads?.toLocaleString?.()} testId="ps-downloads" />
          <StatTile icon="📣" label="Community Shares" value={stats.community_shares?.toLocaleString?.()} testId="ps-shares" />
          <StatTile icon="🎁" label="Gifts Received" value={stats.gifts_received?.toLocaleString?.()} testId="ps-gifts" />
          <StatTile icon="🪙" label="Gift Coins Received" value={giftCoinValue.toLocaleString()} testId="ps-gift-coins"
                   accent="bg-amber-500/10 border-amber-500/30" />
          <StatTile icon="🏆" label="Featured" value={stats.featured_count?.toLocaleString?.()} testId="ps-featured" />
        </div>

        {/* Money & conversion row */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-6">
          <StatTile
            icon="💰"
            label="Sales (paid)"
            value={stats.sales?.toLocaleString?.()}
            accent="bg-emerald-500/10 border-emerald-500/30"
            testId="ps-sales"
          />
          <StatTile
            icon="💵"
            label="Revenue"
            value={`$${revenueDollars.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
            accent="bg-emerald-500/10 border-emerald-500/30"
            testId="ps-revenue"
          />
          <StatTile
            icon="📈"
            label="Conversion Rate"
            value={`${stats.conversion_rate_pct ?? 0}%`}
            sub="(sales + downloads) / plays"
            accent="bg-amber-500/10 border-amber-500/30"
            testId="ps-conversion"
          />
        </div>

        {/* CTAs */}
        <div className="flex flex-wrap gap-2 mt-4">
          {isOwn && (
            <Link to="/beats/submit">
              <Button className="bg-gradient-to-r from-purple-600 to-pink-600 text-white" data-testid="ps-upload-beat">
                + Upload a beat
              </Button>
            </Link>
          )}
          <Link to="/discover#beats">
            <Button variant="outline" className="text-white border-gray-700" data-testid="ps-marketplace">
              Open Beat Marketplace
            </Button>
          </Link>
          {isOwn && (
            <Link to="/admin/payouts">
              <Button variant="outline" className="text-white border-gray-700">
                💰 Payouts
              </Button>
            </Link>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProducerStatsPage;
